import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { formatDayTime, withCountRu } from '@dorixona/utils'
import { isApiError } from '@dorixona/api'
import { Icon } from '../../ui/Icon'
import { Modal } from '../../ui/Modal'
import { Failed, failureText } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { useSession } from '../auth/session'
import {
  useAddMapKey,
  useCheckMapKey,
  useDeleteMapKey,
  useMapKeys,
  useSaveMapKeys,
  type MapKey,
} from './api'
import {
  MAP_KEY_STATUS_TEXT,
  MAP_KEY_STATUS_TONE,
  changedMapKeys,
  mapKeyError,
  nextMapKeySort,
  reorderMapKeys,
  sortedMapKeys,
} from './model'

export interface MapKeysProps {
  onToast: (message: string) => void
}

/** Имя ключа для подписей: своя подпись или начало самого ключа. */
const keyName = (key: MapKey) => key.label || `${key.apiKey.slice(0, 8)}…`

function announcements(name: (id: unknown) => string): Announcements {
  return {
    onDragStart: ({ active }) => `Взяли ключ «${name(active.id)}»`,
    onDragOver: ({ active, over }) =>
      over ? `«${name(active.id)}» над «${name(over.id)}»` : `«${name(active.id)}» вне списка`,
    onDragEnd: ({ active, over }) =>
      over ? `«${name(active.id)}» поставлен на место «${name(over.id)}»` : 'Порядок не изменён',
    onDragCancel: ({ active }) => `Перенос «${name(active.id)}» отменён`,
  }
}

const INSTRUCTIONS =
  'Чтобы поменять порядок, нажмите пробел, стрелками вверх и вниз выберите место и нажмите пробел ещё раз. Escape — отмена.'

interface SortableRowProps {
  mapKey: MapKey
  draggable: boolean
  children: ReactNode
}

/** Строка ключа: тянут за ручку слева, в строке есть переключатель и кнопки. */
function SortableRow({ mapKey, draggable, children }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: mapKey.id,
    disabled: !draggable,
  })
  const style: CSSProperties = {
    transform: transform ? `translate3d(0, ${Math.round(transform.y)}px, 0)` : undefined,
    transition,
  }
  return (
    <tr ref={setNodeRef} style={style} data-dragging={isDragging || undefined}>
      <td className="mk-grip">
        {draggable ? (
          <button
            type="button"
            className="ui-iconbtn"
            aria-label={`Перетащить ключ «${keyName(mapKey)}»`}
            {...attributes}
            {...listeners}
          >
            <Icon name="i-grip" />
          </button>
        ) : null}
      </td>
      {children}
    </tr>
  )
}

/**
 * Пул ключей Яндекс Карт (`/admin/maps/keys`). Карта и геокодер в обоих
 * приложениях берут первый рабочий ключ по порядку; Яндекс отказал — сервер
 * переходит к следующему, исчерпанный ключ отдыхает до полуночи по Москве.
 * Здесь ключи добавляют, включают, ставят по порядку и проверяют.
 */
export function MapKeys({ onToast }: MapKeysProps) {
  const { can } = useSession()
  const list = useMapKeys()
  const save = useSaveMapKeys()
  const check = useCheckMapKey()
  const remove = useDeleteMapKey()
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<MapKey | null>(null)
  // Пока порядок сохраняется, показываем уже новый: строка не прыгает назад.
  const [pending, setPending] = useState<MapKey[] | null>(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const canCreate = can('integrations', 'c')
  const canUpdate = can('integrations', 'u')
  const canDelete = can('integrations', 'd')
  const all = pending ?? sortedMapKeys(list.data ?? [])
  const name = (id: unknown) => {
    const found = all.find((k) => k.id === id)
    return found ? keyName(found) : ''
  }
  // Работающих сегодня: включён и Яндекс его не отверг.
  const usable = all.filter((k) => k.active && k.status !== 'exhausted' && k.status !== 'invalid')

  const failed = (error: unknown) => {
    onToast(failureText(error))
  }

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return
    const next = reorderMapKeys(all, Number(active.id), Number(over.id))
    setPending(next)
    save.mutate(changedMapKeys(all, next), {
      onSuccess: () => {
        onToast('Порядок ключей изменён')
      },
      onError: failed,
      onSettled: () => {
        setPending(null)
      },
    })
  }

  function toggle(key: MapKey, active: boolean) {
    save.mutate([{ ...key, active }], {
      onSuccess: () => {
        onToast(active ? `Ключ «${keyName(key)}» включён` : `Ключ «${keyName(key)}» выключен`)
      },
      onError: failed,
    })
  }

  function checkKey(key: MapKey) {
    check.mutate(key.id, {
      onSuccess: (checked) => {
        onToast(`Ключ «${keyName(checked)}»: ${MAP_KEY_STATUS_TEXT[checked.status]}`)
      },
      onError: failed,
    })
  }

  return (
    <section className="ui-table-card mk-card" aria-labelledby="int-map-keys">
      <div className="ui-card-head">
        <h3 id="int-map-keys">Ключи Яндекс Карт</h3>
        {list.data ? (
          <span
            className="ui-count"
            aria-label={`${withCountRu(usable.length, ['ключ работает', 'ключа работают', 'ключей работают'])} из ${all.length}`}
          >
            {usable.length} из {all.length}
          </span>
        ) : null}
        {canCreate ? (
          <button
            type="button"
            className="ui-btn"
            data-size="sm"
            onClick={() => {
              setAdding(true)
            }}
          >
            <Icon name="i-plus" />
            Добавить ключ
          </button>
        ) : null}
      </div>

      {list.isPending ? <TableSkeleton what="ключи" rows={3} /> : null}
      {list.isError ? (
        <Failed
          what="ключи"
          error={list.error}
          onRetry={() => {
            void list.refetch()
          }}
        />
      ) : null}
      {list.data && all.length === 0 ? (
        <div className="ui-empty">
          <Icon name="i-key" />
          <b>Ключей в пуле нет</b>
          <p>Пока ключ не добавлен, карты в приложениях не загрузятся.</p>
        </div>
      ) : null}

      {list.data && all.length > 0 ? (
        <div className="ui-table-wrap">
          <table className="ui-table mk-table">
            <colgroup>
              <col className="w-grip" />
              <col />
              <col className="w-state" />
              <col className="w-checked" />
              <col className="w-on" />
              <col className="w-actions" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">
                  <span className="visually-hidden">Порядок</span>
                </th>
                <th scope="col">Ключ</th>
                <th scope="col">Состояние</th>
                <th scope="col">Проверен</th>
                <th scope="col" data-align="center">
                  Включён
                </th>
                <th scope="col">
                  <span className="visually-hidden">Действия</span>
                </th>
              </tr>
            </thead>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
              accessibility={{
                announcements: announcements(name),
                screenReaderInstructions: { draggable: INSTRUCTIONS },
              }}
            >
              <SortableContext items={all.map((k) => k.id)} strategy={verticalListSortingStrategy}>
                <tbody>
                  {all.map((key) => (
                    <SortableRow
                      key={key.id}
                      mapKey={key}
                      draggable={canUpdate && all.length > 1 && !save.isPending}
                    >
                      <td>
                        <div className="txt mk-key">
                          <span className="primary">{key.label || 'Без подписи'}</span>
                          <span className="ui-code">{key.apiKey}</span>
                        </div>
                      </td>
                      <td>
                        <div className="mk-state">
                          <span className="ui-status" data-tone={MAP_KEY_STATUS_TONE[key.status]}>
                            {MAP_KEY_STATUS_TEXT[key.status]}
                          </span>
                          {key.status === 'exhausted' && key.exhaustedUntil ? (
                            <span className="secondary">
                              до {formatDayTime(key.exhaustedUntil)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="mk-checked">
                        {key.checkedAt ? formatDayTime(key.checkedAt) : '—'}
                      </td>
                      <td data-align="center">
                        {canUpdate ? (
                          <label className="ui-switch" data-size="sm">
                            <input
                              type="checkbox"
                              role="switch"
                              checked={key.active}
                              disabled={save.isPending}
                              aria-label={`Выдавать ключ «${keyName(key)}»`}
                              onChange={(e) => {
                                toggle(key, e.target.checked)
                              }}
                            />
                          </label>
                        ) : key.active ? (
                          'да'
                        ) : (
                          'нет'
                        )}
                      </td>
                      <td>
                        <div className="mk-actions">
                          {canUpdate ? (
                            <button
                              type="button"
                              className="ui-iconbtn"
                              aria-label={`Проверить ключ «${keyName(key)}»`}
                              data-tip="Проверить у Яндекса"
                              disabled={check.isPending}
                              aria-busy={check.isPending && check.variables === key.id}
                              onClick={() => {
                                checkKey(key)
                              }}
                            >
                              <Icon name="i-sync" />
                            </button>
                          ) : null}
                          {canDelete ? (
                            <button
                              type="button"
                              className="ui-iconbtn ui-danger-icon"
                              aria-label={`Удалить ключ «${keyName(key)}»`}
                              onClick={() => {
                                setRemoving(key)
                              }}
                            >
                              <Icon name="i-trash" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </SortableRow>
                  ))}
                </tbody>
              </SortableContext>
            </DndContext>
          </table>
        </div>
      ) : null}

      {adding ? (
        <AddMapKeyDialog
          sort={nextMapKeySort(all)}
          onToast={onToast}
          onClose={() => {
            setAdding(false)
          }}
        />
      ) : null}

      {removing ? (
        <Modal
          title="Удалить ключ?"
          onClose={() => {
            setRemoving(null)
          }}
        >
          <p>
            Ключ «{keyName(removing)}» больше не будет выдаваться картам. Вернуть его можно, добавив
            заново.
          </p>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setRemoving(null)
              }}
            >
              Не удалять
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              disabled={remove.isPending}
              aria-busy={remove.isPending}
              onClick={() => {
                remove.mutate(removing.id, {
                  onSuccess: () => {
                    onToast('Ключ удалён')
                  },
                  onError: failed,
                  onSettled: () => {
                    setRemoving(null)
                  },
                })
              }}
            >
              Удалить
            </button>
          </div>
        </Modal>
      ) : null}
    </section>
  )
}

interface AddMapKeyDialogProps {
  /** Новый ключ встаёт последним. */
  sort: number
  onClose: () => void
  onToast: (message: string) => void
}

/** Новый ключ: сам ключ из кабинета Яндекса и подпись, чтобы отличать ключи. */
function AddMapKeyDialog({ sort, onClose, onToast }: AddMapKeyDialogProps) {
  const add = useAddMapKey()
  const [label, setLabel] = useState('')
  const [apiKey, setApiKey] = useState('')
  // Ошибку показываем после первой попытки: пустое поле при открытии — не ошибка.
  const [tried, setTried] = useState(false)
  const [taken, setTaken] = useState(false)
  const invalid = mapKeyError(apiKey)
  const error = taken ? 'Такой ключ уже есть в пуле' : tried ? invalid : null

  function submit(event: FormEvent) {
    event.preventDefault()
    setTried(true)
    if (invalid) return
    add.mutate(
      { label, apiKey, sort },
      {
        onSuccess: () => {
          onToast('Ключ добавлен')
          onClose()
        },
        onError: (failure) => {
          if (isApiError(failure) && failure.code === 'conflict') setTaken(true)
          else onToast(failureText(failure))
        },
      },
    )
  }

  return (
    <Modal title="Новый ключ Яндекс Карт" onClose={onClose}>
      <form className="ui-form" noValidate onSubmit={submit}>
        <div className="ui-field">
          <label className="ui-label" htmlFor="mk-key">
            Ключ
          </label>
          <input
            id="mk-key"
            placeholder="Вставьте ключ из кабинета Яндекса"
            className="ui-input mk-key-input"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'mk-key-error' : 'mk-key-hint'}
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value)
              setTaken(false)
            }}
          />
          {error ? (
            <span className="ui-error" id="mk-key-error">
              <Icon name="i-alert" />
              {error}
            </span>
          ) : (
            <span className="ui-hint" id="mk-key-hint">
              «JavaScript API и HTTP Геокодер», с доменами 03dorixona.uz и staging.03dorixona.uz
            </span>
          )}
        </div>
        <div className="ui-field">
          <label className="ui-label" htmlFor="mk-label">
            Подпись
          </label>
          <input
            id="mk-label"
            className="ui-input"
            autoComplete="off"
            maxLength={100}
            placeholder="Например, «Основной»"
            value={label}
            onChange={(e) => {
              setLabel(e.target.value)
            }}
          />
        </div>
        <div className="mfoot">
          <button type="button" className="ui-btn" data-variant="secondary" onClick={onClose}>
            Отмена
          </button>
          <button
            type="submit"
            className="ui-btn"
            disabled={add.isPending}
            aria-busy={add.isPending}
          >
            Добавить ключ
          </button>
        </div>
      </form>
    </Modal>
  )
}
